import React, { FC } from 'react';
import { Chip, IconButton, Tooltip } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BlockIcon from '@mui/icons-material/Block';
import ReplayIcon from '@mui/icons-material/Replay';
import styled from 'styled-components';
import { NotificationRecipientStatus } from '../../../Types/TestReports.types';

type Props = {
	who: 'Student' | 'Parent';
	/** SMS never advances past 'sent' (no delivery receipts); WhatsApp uses the full lattice. */
	channel: 'sms' | 'whatsapp';
	status?: NotificationRecipientStatus | null;
	errorCode?: string | null;
	error?: string | null;
	updatedAt?: string | null;
	/** Undefined = no resend offered (feature off). */
	onResend?: () => void;
	resending?: boolean;
};

const SKIP_REASONS: Record<string, string> = {
	no_phone: 'no phone number on file',
	invalid_phone: 'phone number could not be normalised',
	duplicate_of_student: 'same number as the student — not sent twice',
	attempt_missing: 'attempt no longer exists',
	not_graded: 'attempt not graded yet',
};

/**
 * One (recipient, channel) exam-result delivery state, as a small chip
 * (S·SMS / S·WA / P·SMS / P·WA) coloured by lifecycle, with the
 * provider's reason on hover and — when the table passes onResend — a
 * resend control (WhatsApp only; SMS is one-shot, its chips are
 * status-only). In flight (pending/accepted) cannot be resent; a
 * delivered/read message can — a deliberate duplicate, confirmed by the
 * table.
 */
const NotificationRecipientCell: FC<Props> = ({
	who,
	channel,
	status,
	errorCode,
	error,
	updatedAt,
	onResend,
	resending,
}) => {
	const channelShort = channel === 'sms' ? 'SMS' : 'WA';
	const channelLabel = channel === 'sms' ? 'SMS' : 'WhatsApp';
	const inFlight = status === 'pending' || status === 'accepted';
	const detail = (() => {
		if (!status) return 'not sent';
		if (status === 'failed') return `failed${errorCode ? ` (${errorCode})` : ''}${error ? ` — ${error}` : ''}`;
		if (status === 'skipped') return `skipped — ${SKIP_REASONS[error ?? ''] ?? error ?? 'not applicable'}`;
		return status;
	})();
	const when = updatedAt ? ` · ${new Date(updatedAt).toLocaleString()}` : '';

	const visual = (() => {
		switch (status) {
			case 'pending':
			case 'accepted':
				return { color: 'default' as const, icon: <HourglassEmptyIcon />, variant: 'outlined' as const };
			case 'sent':
				return { color: 'info' as const, icon: <DoneIcon />, variant: 'outlined' as const };
			case 'delivered':
				return { color: 'success' as const, icon: <DoneAllIcon />, variant: 'outlined' as const };
			case 'read':
				return { color: 'success' as const, icon: <DoneAllIcon />, variant: 'filled' as const };
			case 'failed':
				return { color: 'error' as const, icon: <ErrorOutlineIcon />, variant: 'outlined' as const };
			case 'skipped':
				return { color: 'default' as const, icon: <BlockIcon />, variant: 'outlined' as const };
			default:
				return null;
		}
	})();

	return (
		<Cell>
			<Tooltip title={`${who} ${channelLabel}: ${detail}${when}`} placement="top">
				{visual ? (
					<Chip
						size="small"
						label={`${who[0]}·${channelShort}`}
						color={visual.color}
						variant={visual.variant}
						icon={visual.icon}
					/>
				) : (
					<Muted>
						{who[0]}·{channelShort} —
					</Muted>
				)}
			</Tooltip>
			{onResend && (
				<Tooltip
					title={
						inFlight
							? `${who} ${channelLabel}: message in flight`
							: `Resend ${channelLabel} to ${who.toLowerCase()}`
					}
					placement="top"
				>
					<span>
						<IconButton
							size="small"
							disabled={inFlight || resending}
							onClick={(event) => {
								event.stopPropagation();
								onResend();
							}}
						>
							<ReplayIcon fontSize="inherit" />
						</IconButton>
					</span>
				</Tooltip>
			)}
		</Cell>
	);
};

const Cell = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 0.125rem;
`;

const Muted = styled.span`
	color: rgba(0, 0, 0, 0.38);
	font-size: 0.8125rem;
	padding: 0 0.25rem;
`;

export default NotificationRecipientCell;
